import { gql } from "@apollo/client";

export const GET_BUSINESS_VIA_IDENTIFIER = gql`
  query Business($bookingUrlIdentifier: String!) {
    business(bookingUrlIdentifier: $bookingUrlIdentifier) {
      about
      acceptsOnlineBookings
      bookingManagement
      bookingUrlIdentifier
      showPromosOnBookingSite
      showFeaturedServicesOnBookingSite
      productStorefrontEnabled
      productStorefrontAvailable
      listProductsOnBookingSite
      giftCardSettingForBooking {
        id
        name
        description
        enabled
        minAmount
        maxAmount
        online
        inStore
      }
      callingCode
      cancellationPolicy
      countryCode
      timezone
      termsAndConditions
      headerImageUrl
      headerImageUrls {
        id
        imageUrl
      }
      id
      allowServiceStaffSelection
      allowGroupBooking
      singleStaffBooking
      selectAnyStaffForOnlineBooking
      logoUrl
      name
      ownerId
      phone
      slug
      businessSpecialties {
        id
        name
        description
      }
      country {
        name
        code
        emojiFlag
        collectionsSupported
        payoutsSupported
        timezone
        utcOffset
        currency {
          code
          factor
          symbol
        }
      }
      salons(bookingVisible: true) {
        address
        timezone
        bearsProcessingFee
        branchName
        callingCode
        countryCode
        city
        email
        id
        isTaxVisible
        locationType
        phone
        state
        status
        urlIdentifier
        bookingLink
        taxInclusivePricing
        packages {
          activateTax
          customPrice
          description
          id
          isDiscountApplicable
          isPurchasableAsVoucher
          name
          salonId
          allowSaleOn
          status
          totalPrice
          usesCustomPrice
          validityMonths
          packageServices {
            duration
            id
            name
            packageId
            price
            quantity
            serviceId
          }
        }
      }
    }
  }
`;

export const GET_SALON_FOR_BOOKING = gql`
  query SalonForBooking($salonId: ID!) {
    salonForBooking(salonId: $salonId) {
      address
      timezone
      bearsProcessingFee
      branchName
      callingCode
      countryCode
      city
      email
      id
      isTaxVisible
      locationType
      phone
      state
      status
      taxInclusivePricing
      bookingLink
      salonStaffs {
        role
        id
        active
        canServeCustomers
        user {
          firstName
          fullName
          lastName
        }
        specialty
      }
      bundles {
        id
        name
        active
        totalPrice
        discountPercentage
        description
        scheduleType
        usesCustomPrice
        customPrice
        bundleServices {
          id
          serviceId
          quantity
          sortOrder
          service {
            id
            name
            price
            duration
            extraTime
            isPublic
          }
        }
      }
      services {
        customerCanSchedule
        description
        duration
        id
        isDepositRequired
        name
        isPublic
        featured
        featuredIndex
        price
        pricingType
        salonId
        hasVariants
        serviceVariants {
          active
          addedById
          createdAt
          deletedAt
          depositType
          depositValue
          description
          duration
          id
          isDepositRequired
          name
          price
          service {
            id
          }
          serviceId
          sortOrder
          updatedAt
        }
        category {
          createdAt
          id
          name
          description
          updatedAt
          orderIndex
        }
        serviceStaffs {
          id
          serviceId
          price
          salonStaff {
            id
            active
            specialty
            role
            user {
              fullName
              firstName
              lastName
            }
          }
        }
      }
      popularServices {
        customerCanSchedule
        description
        duration
        featuredIndex
        id
        isDepositRequired
        name
        isPublic
        price
        pricingType
        salonId
        hasVariants
        serviceVariants {
          active
          depositType
          depositValue
          description
          duration
          id
          isDepositRequired
          name
          price
          serviceId
          sortOrder
        }
        category {
          id
          name
          description
          orderIndex
        }
      }
      salonHours {
        closeTime
        createdAt
        day
        id
        openTime
        updatedAt
      }
    }
  }
`;

export const GET_BUNDLES_FOR_BOOKING = gql`
  query Bundles($salonId: ID, $active: Boolean) {
    bundles(salonId: $salonId, active: $active) {
      id
      name
      active
      category {
        id
        name
        description
      }
      totalPrice
      discountPercentage
      description
      scheduleType
      usesCustomPrice
      customPrice
      bundleServices {
        id
        serviceId
        quantity
        sortOrder
        service {
          id
          name
          price
          duration
          extraTime
          isPublic
        }
      }
    }
  }
`;

export const GET_FAQS = gql`
  query GetFaqs($salonId: ID!) {
    faqs(salonId: $salonId) {
      id
      question
      answer
      businessId
      createdAt
      updatedAt
    }
  }
`;

export const SEARCH_CLIENT = gql`
  query ClientSearch($salonId: ID!, $phone: String!, $countryCode: String!) {
    clientSearch(salonId: $salonId, phone: $phone, countryCode: $countryCode) {
      id
      businessId
      salonId
      createdAt
      updatedAt
      firstName
      lastName
      email
      phone
      callingCode
      countryCode
      address
      walletBalance
      membershipWalletBalance
      blacklisted
      clientMembership {
        id
        membershipStatus
        membership {
          id
          name
          rewardPercentage
          services {
            id
            name
          }
        }
      }
    }
  }
`;

export const CREATE_CLIENT = gql`
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input) {
      client {
        id
        businessId
        salonId
        firstName
        lastName
        email
        phone
        callingCode
        countryCode
        address
      }
      errors {
        message
        property
      }
    }
  }
`;

export const APPOINTMENT_PREVIEW = gql`
  mutation AppointmentPreview($input: AppointmentPreviewInput!) {
    appointmentPreview(input: $input) {
      clientMutationId
      status
      data {
        addOnAmount
        depositAmount
        cancellationFeeMessages
        processingFeeAmount
        depositProcessingFeeAmount
        taxAmount
        subTotal
        totalPaid
        totalPoints
        totalPrice
        totalProductsAmount
        totalServicesAmount
        loyaltyDiscountAmount
        discountAmount
        cancellationFee
        cancellationFeeMessage
        appointmentPromo {
          amount
          title
        }
        feedbackMessages {
          message
          type
        }
      }
      errors {
        message
        property
      }
    }
  }
`;

export const CREATE_APPOINTMENT = gql`
  mutation CreateAppointment($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      status
      appointment {
        id
        bookingId
        startAt
        endAt
        timezone
        client {
          id
        }
        appointmentServices {
          id
          name
          price
          duration
          membershipDiscountAmount
        }
      }
      paymentAuthorization {
        accessCode
        reference
        authorizationUrl
      }
      initiatePayment
      amountToPay
      processingFeeAmount
      errors {
        property
        message
      }
    }
  }
`;

export const CREATE_APPOINTMENT_WITH_CLIENT_SECRET = gql`
  mutation CreateAppointmentWithClientSecret($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      status
      appointment {
        id
        bookingId
        startAt
        endAt
        timezone
        client {
          id
        }
        appointmentServices {
          id
          name
          price
          duration
          membershipDiscountAmount
        }
      }
      paymentAuthorization {
        accessCode
        clientSecret
        reference
        authorizationUrl
      }
      initiatePayment
      amountToPay
      processingFeeAmount
      errors {
        property
        message
      }
    }
  }
`;

export const CONFIRM_STRIPE_APPOINTMENT_PAYMENT = gql`
  mutation ConfirmStripeAppointmentPayment($input: ConfirmStripeAppointmentPaymentInput!) {
    confirmStripeAppointmentPayment(input: $input) {
      status
      appointment {
        id
        bookingId
        startAt
        endAt
        timezone
      }
      errors {
        message
        property
      }
    }
  }
`;

export const CREATE_VOUCHER_PURCHASE = gql`
  mutation CreateVoucher($input: CreateVoucherInput!) {
    createVoucher(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      data {
        discountAmount
        processingFeeAmount
        subtotal
        taxAmount
        totalAmountPayable
        paymentAuthorization {
          accessCode
          clientSecret
          authorizationUrl
          reference
        }
      }
    }
  }
`;

export const VOUCHER_PREVIEW = gql`
  mutation VoucherPreview($input: VoucherPreviewInput!) {
    voucherPreview(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      data {
        discountAmount
        processingFeeAmount
        subtotal
        taxAmount
        totalAmountPayable
      }
    }
  }
`;

export const CREATE_GIFT_CARD_PURCHASE = gql`
  mutation CreateGiftCardPurchase($input: CreateGiftCardPurchaseInput!) {
    createGiftCardPurchase(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      giftCardPurchase {
        id
        amount
        status
        paymentReference
        createdAt
        recipientClient {
          id
          firstName
          lastName
          email
          phone
          countryCode
          callingCode
        }
        buyerClient {
          id
          firstName
          lastName
          email
          phone
          countryCode
          callingCode
        }
      }
    }
  }
`;

export const INITIATE_GIFT_CARD_PURCHASE = gql`
  mutation InitiateGiftCardPurchase($input: InitiateGiftCardPurchaseInput!) {
    initiateGiftCardPurchase(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      data {
        amount
        processingFeeAmount
        totalAmountPayable
        paymentReference
        paymentAuthorization {
          accessCode
          clientSecret
          authorizationUrl
          reference
        }
      }
    }
  }
`;

export const GET_PRODUCTS_FOR_BOOKING = gql`
  query ProductsForBooking($salonId: ID!) {
    salonForBooking(salonId: $salonId) {
      id
      products {
        id
        name
        description
        imageUrl
        retailPrice
        stockCount
        isPurchasable
        expirationDate
        status
        salonId
        category {
          id
          name
        }
      }
    }
  }
`;

export const PRODUCT_PURCHASE_PREVIEW = gql`
  mutation ProductPurchasePreview($input: ProductPurchasePreviewInput!) {
    productPurchasePreview(input: $input) {
      status
      errors {
        property
        message
      }
      data {
        subtotal
        productsTaxAmount
        processingFeeAmount
        totalAmountPayable
        walletPayableAmount
        clientWalletBalance
        canPayWithClientWallet
        invoiceNumber
        paymentReference
      }
    }
  }
`;

export const INITIATE_PRODUCT_PURCHASE = gql`
  mutation InitiateProductPurchase($input: InitiateProductPurchaseInput!) {
    initiateProductPurchase(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      data {
        subtotal
        productsTaxAmount
        processingFeeAmount
        totalAmountPayable
        walletPayableAmount
        clientWalletBalance
        canPayWithClientWallet
        invoiceNumber
        paymentReference
        paymentAuthorization {
          authorizationUrl
          accessCode
          clientSecret
          reference
          paymentAttemptId
        }
        productOrder {
          id
          status
          invoiceNumber
          paymentReference
          totalPrice
          items {
            name
            quantity
            price
          }
        }
      }
    }
  }
`;

export const PRODUCT_ORDER_SUMMARY = gql`
  query ProductOrderSummary($paymentReference: String, $invoiceNumber: String) {
    productOrderSummary(
      paymentReference: $paymentReference
      invoiceNumber: $invoiceNumber
    ) {
      message
      invoiceNumber
      status
      productOrder {
        id
        status
        amountPaid
        totalPrice
        paidAt
        items {
          name
          quantity
          price
        }
      }
    }
  }
`;

export const GET_APPOINTMENT_SUMMARY = gql`
  query GetAppointmentSummary($id: String!) {
    appointmentSummary(id: $id) {
      id
      appointmentDate
      appointmentTime
      status
      totalAmount
      salonId
      clientId
      services {
        id
        name
        price
        duration
      }
      staff {
        id
        firstName
        lastName
      }
    }
  }
`;

export const GET_SERVICE_STAFFS = gql`
  query ServiceStaffs($serviceIds: [ID!]!) {
    serviceStaffs(serviceIds: $serviceIds) {
      salonStaffsForBooking {
        active
        canServeCustomers
        id
        role
        specialty
        salons {
          id
        }
        user {
          firstName
          fullName
          id
          lastName
        }
      }
    }
  }
`;

export const SALON_AVAILABILITY = gql`
  query SalonAvailability(
    $salonId: ID!
    $services: [AvailabilityInput!]!
    $startDate: String!
    $endDate: String!
  ) {
    salonAvailability(
      salonId: $salonId
      services: $services
      startDate: $startDate
      endDate: $endDate
    ) {
      date
      results {
        startTime
        endTime
      }
    }
  }
`;
